import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap, takeUntil, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/apikeys.actions';
import { MessageService } from 'primeng/api';
import { ApiDefOrganizationData } from '@api/models/api-def-organization-data';
import { ApiDefApiKeyData } from '@api/models/api-def-api-key-data';

@Injectable()
export class OrganizationsApiKeysEffects {
  loadOrganizationApiKeys$;
  createOrganizationApiKey$;
  updateOrganizationApiKey$;
  deleteOrganizationApiKey$;
  loadOrganizationApiKeyById$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService
  ) {
    this.loadOrganizationApiKeys$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationApiKeys),
        mergeMap(({ organizationId }) =>
          this.organizationsService.organizationsListApiKeys({ organizationId }).pipe(
            map(apiKeys => OrganizationsActions.loadOrganizationApiKeysSuccess({ organizationId, apiKeys })),
            catchError(error => of(OrganizationsActions.loadOrganizationApiKeysFailure({ organizationId, error }))),
            takeUntil(
              this.actions$.pipe(
                ofType(OrganizationsActions.cancelLoadOrganizationApiKeys),
                filter(cancel => cancel.organizationId === organizationId)
              )
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.createOrganizationApiKey$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.createOrganizationApiKey),
        mergeMap(({ organizationId, body }) =>
          this.organizationsService.organizationsCreateApiKey({ organizationId, body }).pipe(
            map(response => OrganizationsActions.createOrganizationApiKeySuccess({ organizationId, apiKey: response.apiKey!, plainKey: response.plainKey! })),
            catchError(error => of(OrganizationsActions.createOrganizationApiKeyFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateOrganizationApiKey$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateOrganizationApiKey),
        mergeMap(({ organizationId, apiKeyId, body }) =>
          this.organizationsService.organizationsUpdateApiKey({ organizationId, apiKeyId, body }).pipe(
            map(apiKey => OrganizationsActions.updateOrganizationApiKeySuccess({ organizationId, apiKey })),
            catchError(error => of(OrganizationsActions.updateOrganizationApiKeyFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.deleteOrganizationApiKey$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.deleteOrganizationApiKey),
        mergeMap(({ organizationId, apiKeyId }) =>
          this.organizationsService.organizationsDeleteApiKey({ organizationId, apiKeyId }).pipe(
            map(() => OrganizationsActions.deleteOrganizationApiKeySuccess({ organizationId, apiKeyId })),
            catchError(error => of(OrganizationsActions.deleteOrganizationApiKeyFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadOrganizationApiKeyById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationApiKeyById),
        mergeMap(({ organizationId, apiKeyId }) =>
          this.organizationsService.organizationsGetApiKey({ organizationId, apiKeyId }).pipe(
            map(apiKey => OrganizationsActions.loadOrganizationApiKeyByIdSuccess({ organizationId, apiKey })),
            catchError(error => of(OrganizationsActions.loadOrganizationApiKeyByIdFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );
  }
}